import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Bell, ListChecks, HandHeart, Truck, FileText,
  ClipboardCheck, AlertTriangle, CarFront, GraduationCap, Users, Settings,
  Menu, LogOut, Search, ChevronDown, UserCircle, ShieldCheck, UserPlus, IdCard, CreditCard, X
} from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useAuth } from '@/context/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import client, { API } from '@/lib/api';

const ROLE_LABELS = {
  developer: 'Developer (Super Admin)', company_admin: 'Company Admin', der: 'DER',
  owner_operator: 'Owner Operator', driver: 'Driver',
};

// Build a cache-busted URL to the active workspace logo (per-tenant, public endpoint).
function tenantLogoUrl(tenant) {
  if (!tenant?.has_logo || !tenant?.tenant_id) return null;
  return `${API}/tenant/logo/${tenant.tenant_id}?v=${tenant.logo_version || 0}`;
}

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: 'all' },
  { to: '/my-info', label: 'My Info', icon: IdCard, roles: 'driver' },
  { to: '/notifications', label: 'Notifications', icon: Bell, roles: 'all' },
  { to: '/todos', label: 'To-Dos', icon: ListChecks, roles: 'all' },
  { to: '/service-requests', label: 'Service Requests', icon: HandHeart, roles: 'all' },
  { to: '/drivers', label: 'Drivers', icon: Truck, roles: 'staff' },
  { to: '/fleet', label: 'Fleet', icon: CarFront, roles: 'all' },
  { to: '/documents', label: 'Documents', icon: FileText, roles: 'all' },
  { to: '/inspections', label: 'Inspections & Violations', icon: ClipboardCheck, roles: 'staff' },
  { to: '/violations', label: 'Violations', icon: AlertTriangle, roles: 'staff' },
  { to: '/accidents', label: 'Accidents', icon: CarFront, roles: 'staff' },
  { to: '/training', label: 'Training', icon: GraduationCap, roles: 'all' },
  { to: '/audit', label: 'Audit-Ready', icon: ShieldCheck, roles: 'staff' },
  { to: '/users', label: 'Users & Roles', icon: Users, roles: 'admin' },
  { to: '/settings', label: 'Settings', icon: Settings, roles: 'admin' },
];

function navFor(role) {
  return NAV.filter((n) => {
    if (n.roles === 'all') return true;
    if (n.roles === 'driver') return role === 'driver';
    if (n.roles === 'staff') return role !== 'driver';
    if (n.roles === 'admin') return ['developer', 'company_admin', 'der', 'owner_operator'].includes(role);
    return true;
  });
}

function SidebarContent({ onNavigate, pending, inbox }) {
  const { user, tenant } = useAuth();
  const items = navFor(user?.role);
  const planName = tenant?.subscription?.plan_name;
  const companyName = tenant?.name || 'Workspace';
  const tInitials = companyName.split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || 'NF';
  const logoUrl = tenantLogoUrl(tenant);
  return (
    <div className="flex h-full flex-col bg-white">
      {/* Tenant brand (active company) */}
      <div className="flex h-16 items-center gap-2.5 border-b px-4">
        {logoUrl ? (
          <img src={logoUrl} alt={companyName} className="h-9 w-9 shrink-0 rounded-lg border object-contain bg-white" data-testid="sidebar-company-logo" />
        ) : (
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--gold))] text-sm font-bold text-[hsl(var(--foreground))]" data-testid="sidebar-company-avatar">{tInitials}</div>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold leading-tight text-foreground" data-testid="sidebar-company-name">{companyName}</div>
          {planName && <div className="text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--gold))]" data-testid="sidebar-plan-badge">{planName} plan</div>}
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={onNavigate}
              data-testid={`sidebar-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, '-')}-link`}
              className={({ isActive }) =>
                `group flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-[hsl(var(--gold))]/15 text-foreground shadow-[inset_3px_0_0_hsl(var(--gold))]'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`
              }
            >
              <span className="flex items-center gap-3">
                <Icon className="h-[18px] w-[18px]" />
                {it.label}
              </span>
              {it.to === '/documents' && pending > 0 && (
                <span className="rounded-full bg-[hsl(var(--info))] px-1.5 py-0.5 text-[10px] font-semibold text-white">{pending}</span>
              )}
              {it.to === '/notifications' && inbox > 0 && (
                <span className="rounded-full bg-[hsl(var(--gold))] px-1.5 py-0.5 text-[10px] font-semibold text-[hsl(var(--foreground))]">{inbox}</span>
              )}
            </NavLink>
          );
        })}
      </nav>
      {/* Platform identity (minimized, subtle) */}
      <div className="border-t p-3" data-testid="platform-footer">
        <div className="flex items-center gap-2 opacity-80">
          <img src="/nuxfleet-logo.png" alt="NuxFleet" className="h-4 w-4 rounded-sm" />
          <div className="leading-none">
            <div className="text-[11px] font-semibold tracking-tight text-foreground">NuxFleet</div>
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Smart Fleet Ops</div>
          </div>
        </div>
        <a href="https://www.servoapex.com" target="_blank" rel="noreferrer" className="mt-1.5 block text-[9px] text-muted-foreground hover:text-[hsl(var(--primary))]">Built by ServoApex Engineering Ltd. © 2026</a>
      </div>
    </div>
  );
}

export default function AppShell({ children, title }) {
  const { user, tenant, logout, getSessionRemainingMs, idleLimitMs } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const [inbox, setInbox] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [sessRemain, setSessRemain] = useState(idleLimitMs || 0);
  const [billingDismissed, setBillingDismissed] = useState(() => sessionStorage.getItem('nf_billing_banner_dismissed') === '1');

  useEffect(() => {
    client.get('/documents?status=pending').then((r) => setPending(r.data.length)).catch(() => {});
    client.get('/inbox').then((r) => setInbox(r.data?.counts?.all || 0)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen || !getSessionRemainingMs) return;
    const tick = () => setSessRemain(getSessionRemainingMs());
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [menuOpen, getSessionRemainingMs]);

  const sessMin = Math.floor(sessRemain / 60000);
  const sessSec = Math.floor((sessRemain % 60000) / 1000);
  const sessLow = sessRemain <= 2 * 60000;

  const initials = (user?.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-[272px] shrink-0 border-r lg:block">
        <div className="sticky top-0 h-screen">
          <SidebarContent pending={pending} inbox={inbox} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* top gold band */}
        <div className="h-1 w-full bg-[hsl(var(--gold))]" />
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-white/90 px-4 backdrop-blur md:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="topbar-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0">
              <SidebarContent pending={pending} inbox={inbox} onNavigate={() => setOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* Active tenant brand in the top navigation (visible where the sidebar is hidden) */}
          <div className="flex items-center gap-2 lg:hidden" data-testid="topbar-company-brand">
            {tenantLogoUrl(tenant) ? (
              <img src={tenantLogoUrl(tenant)} alt={tenant?.name || 'Workspace'} className="h-8 w-8 shrink-0 rounded-lg border object-contain bg-white" data-testid="topbar-company-logo" />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--gold))] text-xs font-bold text-[hsl(var(--foreground))]">{(tenant?.name || 'Workspace').split(' ').filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</div>
            )}
            <span className="max-w-[40vw] truncate text-sm font-semibold" data-testid="topbar-company-name">{tenant?.name || 'Workspace'}</span>
          </div>

          <h1 className="hidden font-display text-xl font-semibold tracking-tight lg:block">{title}</h1>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-lg border bg-muted/50 px-3 py-1.5 md:flex">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input data-testid="topbar-global-search-input" placeholder="Search" className="w-40 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
            </div>
            {['developer', 'company_admin'].includes(user?.role) && (
              <Button variant="outline" size="sm" className="hidden sm:inline-flex" onClick={() => navigate('/users')} data-testid="topbar-invite-button"><UserPlus className="mr-1.5 h-4 w-4" />Invite</Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => navigate('/notifications')} data-testid="topbar-notifications-button">
              <Bell className="h-5 w-5" />
            </Button>
            <DropdownMenu onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors hover:bg-muted" data-testid="topbar-profile-button">
                  <Avatar className="h-8 w-8"><AvatarFallback className="bg-[hsl(var(--primary))] text-xs text-white">{initials}</AvatarFallback></Avatar>
                  <div className="hidden text-left leading-tight sm:block">
                    <div className="text-sm font-medium">{user?.name}</div>
                    <div className="text-[11px] text-muted-foreground">{ROLE_LABELS[user?.role]}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5" data-testid="session-indicator">
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="flex items-center gap-1 text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--success))]" />Session active</span>
                    <span className={`font-mono font-semibold tabular-nums ${sessLow ? 'text-[hsl(var(--danger))]' : 'text-foreground'}`} data-testid="session-countdown">{sessMin}:{String(sessSec).padStart(2, '0')}</span>
                  </div>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-muted">
                    <div className={`h-full rounded-full ${sessLow ? 'bg-[hsl(var(--danger))]' : 'bg-[hsl(var(--success))]'}`} style={{ width: `${Math.max(0, Math.min(100, (sessRemain / (idleLimitMs || 1)) * 100))}%` }} />
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">Auto sign-out when idle · resets on activity</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile"><UserCircle className="mr-2 h-4 w-4" />My Profile</DropdownMenuItem>
                {['developer', 'company_admin', 'owner_operator'].includes(user?.role) && (
                  <DropdownMenuItem onClick={() => navigate('/plans')} data-testid="menu-plan"><CreditCard className="mr-2 h-4 w-4" />Manage plan{tenant?.subscription?.plan_name ? ` · ${tenant.subscription.plan_name}` : ''}</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Log Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 md:px-8">
          {tenant?.subscription?.billing_status === 'onboarding' && !billingDismissed && ['developer', 'company_admin', 'owner_operator'].includes(user?.role) && (
            <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 px-4 py-3" data-testid="billing-reminder-banner">
              <CreditCard className="h-5 w-5 shrink-0 text-[hsl(var(--gold))]" />
              <div className="min-w-0 flex-1 text-sm">
                <span className="font-semibold text-foreground">Finish setting up billing.</span>{' '}
                <span className="text-muted-foreground">Your workspace is active on {tenant?.subscription?.plan_name || 'Corporate'}, but billing isn't set up yet.</span>
              </div>
              <Button size="sm" className="bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" onClick={() => navigate('/plans')} data-testid="billing-reminder-cta">Set up billing</Button>
              <button onClick={() => { sessionStorage.setItem('nf_billing_banner_dismissed', '1'); setBillingDismissed(true); }} className="rounded p-1 text-muted-foreground hover:bg-black/5 hover:text-foreground" aria-label="Dismiss" data-testid="billing-reminder-dismiss"><X className="h-4 w-4" /></button>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
